import * as Sentry from "@sentry/nextjs";

// User-Agents headless / crawlers / outils d'audit. On NE veut PAS tracker
// leurs erreurs — ce ne sont pas des vrais users, et leur runtime déclenche
// fréquemment des erreurs DOM (appendChild null, removeChild null) à cause
// de leur instrumentation injectée. Pollue Sentry sans valeur diagnostique.
// Note : `bot` matche Googlebot/AdsBot mais PAS "GoogleOther" (UA
// `(compatible; GoogleOther)`) ni "Google-InspectionTool" → ajoutés
// explicitement. Ces crawlers exécutent du JS et déclenchent des erreurs
// (ex : .json() sur réponse vide quand rate-limités) qui polluent Sentry
// sans correspondre à un vrai user (SAHHA-X 2026-05-20, /rdv/ noindex).
const NON_HUMAN_UA = /HeadlessChrome|Lighthouse|PageSpeed|GTmetrix|bot|crawler|spider|slurp|preview|monitor|uptime|pingdom|vercel-screenshot|puppeteer|playwright|phantomjs|GoogleOther|Google-InspectionTool|Google-Site-Verification|APIs-Google|FeedFetcher|Storebot/i;

// React 19 inline les scripts de streaming de segments dans le HTML sous des
// noms minifiés à une lettre : $RS (completeSegment), $RC (completeBoundary),
// $RB, $RX, $RT… Sur réseau mobile instable, le flux SSR peut être tronqué :
// un script $RS tardif référence alors un node de segment que le <body> n'a
// jamais reçu → `Cannot read properties of null (reading 'parentNode')`.
// Ce n'est PAS un bug applicatif (aucun de notre code ne s'appelle "$RS"),
// c'est une fragilité du streaming React face aux connexions coupées. On le
// filtre au niveau frame plutôt que par message (le message "parentNode" est
// trop générique et masquerait de vrais bugs DOM dans notre code).
const REACT_STREAMING_FN = /^\$R[A-Z]$/;
function isReactStreamingError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames) return false;
  return frames.some((f) => !!f.function && REACT_STREAMING_FN.test(f.function));
}

// Erreurs déclenchées par des in-app browsers Android (SnapTube downloader,
// TikTok WebView, Facebook in-app browser, etc.) qui injectent leur propre
// bridge JS↔Java pour intercepter les liens. Quand le user navigue ou ferme
// la WebView pendant un setTimeout, l'objet Java côté natif est garbage-
// collected mais le JS hook continue → "Java object is gone" ou JSON.parse
// d'un payload undefined (SAHHA-1A 2026-06-12 + SAHHA-19 2026-06-10, tous
// device=SM-A025F + UA Chrome Mobile WebView 149 + stack `SnapTube.value`,
// `jsReceiveMessages`). 100% bug du natif de l'app, jamais notre code.
const IN_APP_BROWSER_FN = /^(SnapTube|jsReceiveMessages|jsReceiveMessage)$/;
function isInAppBrowserBridgeError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames) return false;
  return frames.some(
    (f) =>
      !!f.function &&
      (IN_APP_BROWSER_FN.test(f.function) ||
        f.function.includes("jsReceiveMessages")),
  );
}

// PostHog heatmaps SDK : bug interne où null.tagName est lu sur un élément
// DOM removed entre le moment où il est trouvé et le moment où on l'inspecte
// (race condition rare entre onRemoteConfig → startIfEnabled). 2 occurrences
// uniques sur 14 jours (SAHHA-Z + SAHHA-10, /en/mind, 2026-05-28). Stack
// frames `heatmaps.onRemoteConfig`, `heatmaps.startIfEnabled`, `heatmaps.Zs`
// → 100 % SDK posthog-js, jamais notre code applicatif. Non-actionnable :
// le hook PostHog ne nous expose pas d'API pour valider l'élément avant.
// Filtré par stack frame plutôt que par message car « null.tagName » est
// trop générique et masquerait de vrais bugs DOM dans notre app.
const POSTHOG_HEATMAP_FN = /^heatmaps\./;
function isPostHogHeatmapError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames) return false;
  return frames.some(
    (f) => !!f.function && POSTHOG_HEATMAP_FN.test(f.function),
  );
}

// Scripts injectés dans le contexte de la page par le navigateur lui-même
// (Chrome iOS / Google app injectent des WKUserScript : leurs frames portent
// l'URL DU DOCUMENT comme filename, fonctions minifiées Ek/Gk/Ik…) ou par des
// extensions non préfixées (Edge "executors/200.js"). Signature commune :
// la stack a des frames, mais AUCUNE dans nos bundles `_next/`. Nos vrais
// bugs ont toujours au moins une frame `_next/static/chunks/*` (React, nos
// composants) — Sentry 7668031275 (299 evt M_ID), 7677856264 / 7670301132 /
// 7686268287 / 7653560890 (Maximum call stack, tous iOS 26 Chrome/Google app).
function isInjectedScriptError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || frames.length === 0) return false;
  const inOurBundles = frames.some((f) => {
    const fn = f.filename || f.abs_path || "";
    return fn.includes("/_next/");
  });
  return !inOurBundles;
}

// Bots/outils d'automatisation qui hookent addEventListener ("addEL_hook",
// console "[jshost]") puis déclenchent des erreurs DOM dans React sur des
// nodes qu'ils ont eux-mêmes retirés. UA spoofé (Safari 18 sur "iOS 11").
// Sentry 7648508305 / 7648530679 / 7648508298 / 7511321847 (null.tagName /).
const AUTOMATION_HOOK_FN = /^(addEL_hook|top\.addEventListener)$/;
function isAutomationHookError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames) return false;
  return frames.some((f) => !!f.function && AUTOMATION_HOOK_FN.test(f.function));
}

// Gestionnaires de mots de passe / autofill des navigateurs in-app Android :
// ils planifient leur routine via setTimeout, donc `isInjectedScriptError` ne
// les voit PAS — le wrapper setTimeout du SDK Sentry laisse une frame `_next/`
// dans la pile (mechanism auto.browser.browserapierrors.setTimeout) et suffit
// à faire croire à du code à nous. Seule la frame qui jette est parlante :
// fonction d'autofill, filename `<anonymous>`.
// Sentry 7741975975 : « xbrowser is not defined » dans execute_auto_fill,
// Chrome WebView Android 15.
const AUTOFILL_FN = /^(execute_auto_fill|autofill_|__xbrowser)/;
function isAutofillExtensionError(event: Sentry.ErrorEvent): boolean {
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (!frames || frames.length === 0) return false;
  // Dans une stack Sentry, la frame qui jette est la DERNIÈRE.
  const throwing = frames[frames.length - 1];
  if (!throwing?.function || !AUTOFILL_FN.test(throwing.function)) return false;
  const file = throwing.filename || throwing.abs_path || "";
  // Exige que la frame fautive soit hors de nos bundles, pour ne jamais
  // masquer un vrai bug qui porterait par hasard un nom voisin.
  return !file.includes("/_next/");
}

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  // Tunnel via notre route handler /monitoring (1st-party, anti-adblock).
  // Sans `tunnel`, le SDK enverrait directement vers ingest.us.sentry.io
  // → bloqué par adblockers + besoin de l'autoriser dans CSP connect-src.
  tunnel: "/monitoring",
  environment: process.env.NEXT_PUBLIC_ENV ?? "development",
  tracesSampleRate: process.env.NEXT_PUBLIC_ENV === "production" ? 0.1 : 0,
  sendDefaultPii: false,
  debug: false,
  // Session Replay RETIRÉ le 2026-09-08. `replayIntegration()` fait entrer
  // @sentry-internal/replay dans `rootMainFiles` du build : 122 483 octets
  // (38 730 gz) chargés sur les 144 718 URLs du site, et surtout une
  // dépendance BLOQUANTE de l'hydratation — en neutralisant ce chunk, la page
  // ne s'hydrate plus du tout.
  //
  // Coût mesuré (profil CDP Chrome, Pixel 5, CPU ×6, sourcemaps prod
  // déminifiés) : 167-217 ms de self-time sur /ar/pharmacie-de-garde/fes,
  // 500 ms sur /ar/neurologue/marrakech — devant react-dom. Remplacé par un
  // stub no-op, les tâches longues passent de 851 à 570 ms sur la page garde
  // et de 2 636 à 1 635 ms sur la fiche médecin. L'INP mobile de l'origine
  // est à 229 ms pour un seuil de 200 (69 % de bonnes visites seulement),
  // et il monte depuis mai (165 → 229 ms) : c'est la seule Core Web Vital
  // en échec, et elle est signal de classement.
  //
  // Ce qui reste intact : la remontée d'erreurs, les filtres ignoreErrors /
  // denyUrls, le tunnel /monitoring, les breadcrumbs et les tags. Seule la
  // relecture vidéo des sessions en erreur est perdue. Si elle redevient
  // nécessaire, la charger à la demande via
  // `Sentry.lazyLoadIntegration("replayIntegration")` depuis `beforeSend`,
  // jamais en intégration statique.
  integrations: [],
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications.",
    "Non-Error promise rejection captured",
    // Variante Sentry 8+ quand l'object thrown a des keys identifiées :
    // "Object captured as promise rejection with keys: code, message".
    // Pattern typique : extensions navigateur (wallets crypto, password
    // managers, Honey, Grammarly) qui rejettent leur propre objet
    // {code, message} dans page context. Pas notre code (jamais de stack
    // frame applicatif). SAHHA-18 2026-06-04 = 1 event /pharmacien
    // Mac Chrome sans stack, classique injection extension.
    /captured as promise rejection with keys/i,
    // iOS Safari : quand l'utilisateur navigue away pendant un fetch RSC
    // (Next.js prefetch ou navigation client), l'AbortController throw un
    // DOM Event{type:'error'} au lieu d'une vraie Error. Sentry capture
    // ça sans stack frame applicatif → title "<unknown>".
    // SAHHA-12 2026-06-11 = 5 events tous Mobile Safari / Chrome iOS sur
    // fiches médecin après nav from /recherche. Pas notre code, pur quirk
    // iOS WebKit promise + AbortController.
    /Event `Event` \(type=error\) captured as promise rejection/i,
    // Extensions wallets crypto (MetaMask, Phantom, Coinbase, Brave Wallet…)
    // injectent window.ethereum dans page context. Quand le user n'a pas
    // configuré l'extension ou que le setter est appelé sans provider actif,
    // ces extensions throw TypeError : "undefined is not an object
    // (evaluating 'window.ethereum.selectedAddress = ...')" — pas notre code.
    // SAHHA-P 2026-06-09 = 7 events sur 24h, 100% sourcé extensions.
    /window\.ethereum/i,
    "Network request failed",
    "Failed to fetch",
    "Load failed",
    "AbortError",
    // Storage bloqué côté browser (mode privé strict, cookies tiers off,
    // certains paramètres Android/iOS). Non-actionnable côté code, le hook
    // useLocalStorage est déjà try/catché — ces erreurs viennent de SDKs
    // tiers (PostHog, NextAuth, etc.) qui ne wrap pas leurs accès.
    /SecurityError.*localStorage/i,
    /SecurityError.*sessionStorage/i,
    "Access is denied for this document",
    // QuotaExceeded : storage plein côté browser (idem, non-actionable)
    "QuotaExceededError",
    // DOM mutated externally par Google Translate/Grammarly/extensions →
    // React commitDeletion crash. Page a translate=no mais les extensions
    // Chrome bypass parfois. Bug environnemental, pas applicatif.
    /removeChild.*not a child of this node/i,
    /insertBefore.*not a child of this node/i,
    // Même cause, autre formulation selon le moteur : React ne retrouve plus
    // le node à démonter parce qu'une extension (ou Google Translate) l'a
    // retiré du DOM. Stack 100 % interne React, aucun frame applicatif —
    // 1 event / 0 user sur Android 10, 2026-09-04.
    /Cannot read propert(y|ies) of null \(reading 'removeChild'\)/i,
    /Cannot read propert(y|ies) of null \(reading 'insertBefore'\)/i,
    "ResizeObserver loop",
    // ChunkLoadError post-deploy : visiteur sur vieux HTML qui référence
    // un chunk hash purgé. Géré par auto-reload dans [locale]/error.tsx.
    /Loading chunk \d+ failed/i,
    /ChunkLoadError/i,
    // Google Maps importLibrary() échoue ("Could not load map/marker/…") :
    // adblocker qui bloque maps.googleapis.com, réseau mobile coupé, ou
    // self-test CSP du SDK. L'erreur est levée DANS le JS interne de
    // Google Maps (maps-api-v3/.../main.js), pas notre code — non
    // actionnable. Le CSP prod autorise bien Maps (vérifié 2026-05-14).
    /Could not load "(map|marker|places|geometry|drawing|visualization|core|maps3d)"/i,
    // SecurityError générique iOS Safari Private Mode : storage/cookie/
    // crypto API restreintes. Le SDK Sentry lui-même + PostHog + Plausible
    // peuvent throw "SecurityError: The operation is insecure" sans context
    // de localStorage explicite. Non-actionnable, bug du browser strict mode.
    /SecurityError.*operation is insecure/i,
    // Hydration mismatch SSR vs CSR Next.js : très souvent transient
    // (extension navigateur qui modifie le HTML avant React, ou variation
    // Date.now()/timezone entre serveur et client). Rare en occurrence
    // unique, jamais reproductible — Next.js v15 a aussi de faux positifs
    // après un HMR partiel. On les filtre vs. en faire des tickets fantômes.
    /^Hydration (Error|failed because)/i,
    "Minified React error #418",
    "Minified React error #421",
    "Minified React error #422",
    "Minified React error #423",
    "Minified React error #425",
    // window.__firefox__ : objet interne injecté par Firefox iOS / Reader
    // Mode. Quand l'injection est partielle ou absente, le script de
    // Firefox lui-même throw ("__firefox__.reader" undefined, "Can't find
    // variable: __firefox__"). 100% navigateur, jamais notre code.
    /__firefox__/,
    // Workers tiers spawned via blob: URL (PostHog session recording,
    // extensions navigateur, scrapers headless qui injectent leur propre
    // worker) qui échouent à charger leur script via importScripts. Le
    // stack frame est `blob:app:///<uuid>` ou `blob:https://.../<uuid>`,
    // jamais notre code. Non-actionnable.
    /Failed to execute 'importScripts' on 'WorkerGlobalScope'/i,
    // SyntaxError "Unexpected token X" : pattern bot scraper qui tronque le
    // stream React 19 puis exécute le JS partiel → `} else {` orphelin sur
    // un chunk minifié. Pré-existant, 10 events / 0 user (SAHHA-13). Le UA
    // guard NON_HUMAN_UA drop déjà la majorité, mais certains scrapers
    // spoofent un UA Chrome légitime. Le filtre est volontairement strict
    // (Unexpected token) pour ne masquer aucun vrai bug de syntaxe.
    /SyntaxError.*Unexpected token/i,
    // SnapTube / TikTok / Facebook in-app browser bridge JS↔Java : message
    // direct quand l'object natif est GC pendant un setTimeout. Filtré aussi
    // par stack frame (`isInAppBrowserBridgeError` dans beforeSend) au cas
    // où le message varie selon la version Android.
    /Java object is gone/i,
    /Error invoking [a-zA-Z]+: Java object is gone/i,
    // Pareil que SyntaxError "Unexpected token" : flux JS tronqué (réseau
    // mobile coupé, bot scraper, WebView ancienne Chrome <50 qui rejette
    // optional chaining). Stack vide `at None (app:///<url>:1)` sans frame
    // applicatif (SAHHA-11 2026-05-28 / SAHHA-1C 2026-06-15 Chrome WebView 44
    // sur Android 8.1 = device 2017 jamais mis à jour).
    /SyntaxError.*Unexpected end of (input|script)/i,
    // controlsList sur undefined : extensions navigateur video-downloader
    // (Video DownloadHelper, Flash Video Downloader, etc.) qui injectent un
    // userscript essayant de set controlsList sur un <video> ou <audio>
    // absent de la page. Pas notre code (vérifié sur /ar/pharmacie-de-garde
    // qui n'a aucun media element). SAHHA-1B 2026-06-14.
    /Cannot set propert(y|ies) .*controlsList/i,
    // _leaflet_pos undefined : bug interne Leaflet quand le component carte
    // unmount pendant que Leaflet manipule la position d'un marker. Race
    // condition cleanup → bug Leaflet upstream non patché. SAHHA-Y récurrent
    // 5 events 2026-05-22→2026-05-31 sur /cardiologue/tetouan (mobile carte
    // physicians). Filter par message (le _leaflet_pos est très spécifique
    // à la lib Leaflet, ne masquera aucun vrai bug applicatif).
    /Cannot read propert(y|ies) of undefined.*_leaflet_pos/i,
    // this._map.getZoom : requestAnimationFrame orphelin de `_transitionEnd`
    // dans @maplibre/maplibre-gl-leaflet (leaflet-maplibre-gl.js:203-218). Le
    // pont planifie un rAF sans jamais en mémoriser l'identifiant — il n'existe
    // aucun cancelAnimFrame dans la lib. Si le composant carte se démonte dans
    // la frame qui suit un resize ou une fin de zoom animé, map.remove() met
    // layer._map à null, puis le rAF tire dessus à la frame suivante.
    // Strictement du démontage : l'erreur n'est atteignable QU'APRÈS
    // map.remove(), donc la carte a déjà disparu de l'écran — aucun impact
    // visiteur possible. Même famille et même arbitrage que _leaflet_pos
    // ci-dessus : on filtre plutôt que de figer des internes de la lib.
    // SAHHA-2F, 1 évènement / 0 utilisateur / 14 j (app Google iOS, /ar après
    // navigation depuis une page carte).
    //
    // Ancré sur `this._map` et PAS sur `getZoom` seul : notre code n'écrit
    // jamais `this._map` (0 occurrence dans src/), donc ce motif ne peut
    // masquer aucun bug à nous — alors qu'un motif sur `getZoom` masquerait le
    // `map.getZoom()` légitime de MapView.tsx. Formulation WebKit uniquement,
    // la seule observée ; si la variante V8 apparaît un jour, la traiter alors.
    /null is not an object.*this\._map\.getZoom/i,
    // SecurityError cross-origin frame : iframe 3rd-party (Google Maps,
    // YouTube embed, OG widget) qui throw quand notre code essaie de lire
    // window.Element / contentDocument depuis une iframe d'origine differente.
    // Bloque par Same-Origin Policy navigateur. SAHHA-1E 2026-06-21 sur
    // /generaliste/casablanca/mohamed-achibane (Chrome 149 Windows). Stack
    // `onIframeLoad` -> `HTMLIFrameElement.<anonymous>` = listener interne
    // Next.js / lib 3rd-party. Non-actionable cote code applicatif (SOP est
    // une feature browser, pas un bug).
    /SecurityError.*Blocked a frame with origin.*cross-origin frame/i,
    /Failed to read a named property .* from .Window./i,
    // Huawei Browser : injecte un script de translation auto qui POST vers
    // searchaggr-dra.dt.dbankcloud.com. CORS / network fail = bruit pur, pas
    // notre code. SAHHA-1J 2026-06-24 sur /ar/orthopediste/beni-mellal
    // (2 events, 0 users). Pattern URL specifique dbankcloud + searchaggr.
    /NetworkError.*XMLHttpRequest.*dbankcloud\.com/i,
    /Failed to (?:load|execute|fetch).*searchaggr-dra\.dt\.dbankcloud/i,
    // CustomError Jsloader apis.google.com : NextAuth GoogleProvider charge
    // gapi qui fail ponctuellement (reseau utilisateur, pas notre code).
    // SAHHA-1F 2026-06-22 (1 event, 0 user).
    /Jsloader error.*apis\.google\.com/i,
    // Error: invalid origin : scanner OAuth / iframe cross-origin sans
    // contexte applicable. SAHHA-1H 2026-06-23 (1 event, 0 user).
    /^Error: invalid origin$/i,
    // null video.pause : extensions video downloader injectent un script
    // sur toutes pages, tentent video.pause meme si pas de <video> sur la
    // page. SAHHA-1G recurrent 2026-06-22 (4 events, 0 user).
    /null is not an object.*video.*\.pause/i,
    /Cannot read propert(y|ies) of null.*video.*\.pause/i,
    // ── Triage Sentry 2026-08-24 ──
    // GTM : un tag Facebook Pixel (event) fire alors que le base code
    // `fbq` n'est pas chargé (adblock ou ordre des tags dans le container).
    // Frames 100 % gtag/js (aussi en denyUrls) — à corriger dans GTM, pas
    // dans le code. Sentry 7641439739.
    /fbq is not defined/i,
    // Extension Chrome/Edge qui appelle chrome.runtime.sendMessage sur un
    // onglet fermé. Sentry 7590488374.
    /Invalid call to runtime\.sendMessage/i,
    // Wallets crypto : "Cannot set property ethereum of #<Window> which has
    // only a getter" (variante sans le mot window.ethereum). Sentry 7636342689.
    /property ethereum of #<Window>/i,
    // Extensions qui tentent de réassigner window.open (popup blockers,
    // download managers). Sentry 7683959004.
    /Cannot assign to read only property 'open' of object '#<Window>'/i,
    // WebView iOS tierce sans bridge natif (window.webkit.messageHandlers
    // absent). Sentry 7632695128.
    /window\.webkit\.messageHandlers/i,
    // Extension qui throw un message brut. Sentry 7636356144.
    /^Error: not found method$/i,
  ],
  denyUrls: [
    /extensions\//i,
    /^chrome:\/\//i,
    /^moz-extension:\/\//i,
    // Blob URLs des workers tiers (PostHog, extensions, scrapers) — cf.
    // ignoreErrors plus haut. Frames stack `blob:app:///<uuid>`.
    /^blob:/i,
    // inpage.js : script injecté par les extensions wallet crypto
    // (MetaMask, Phantom, Brave Wallet, Coinbase, etc.) qui font du
    // postMessage contentScript ↔ inpage. Quand leur RPC échoue, elles
    // throw "Error: func X not found" / "callback id N not found" /
    // "FunctionCallError" dans leur propre code. Jamais le nôtre.
    /inpage\.js/i,
    // PostHog Surveys/Replay/Toolbar SDK chargés via /ingest/static/<file>.js
    // (proxy reverse vers us-assets.i.posthog.com). Bugs internes du SDK
    // (SAHHA-11 surveys.js : null dereference dans le polling Surveys) :
    // non-actionnable côté code applicatif. Bruit pur dans Sentry.
    /\/ingest\/static\//i,
    /us-assets\.i\.posthog\.com/i,
    // Google Tag Manager / gtag internals : erreurs DANS le script Google
    // (tags mal configurés, fbq absent, null.match dans un tag custom) — pas
    // notre code, recommandation Sentry standard. Triage 2026-08-24.
    /gtag\/js/i,
    /googletagmanager\.com/i,
    // Extensions Edge/Chrome dont les scripts sont servis sous un chemin
    // relatif (Edge "executors/NNN.js" → 299 events M_ID, Sentry 7668031275).
    /\/executors\//i,
    /^chrome-extension:\/\//i,
    /^safari(-web)?-extension:\/\//i,
  ],
  beforeSend(event) {
    // Drop events emitted by headless browsers / crawlers / audit tools.
    const ua =
      event.request?.headers?.["User-Agent"] ||
      event.request?.headers?.["user-agent"] ||
      (typeof navigator !== "undefined" ? navigator.userAgent : "") ||
      "";
    if (ua && NON_HUMAN_UA.test(ua)) return null;
    // Drop les crashs des scripts de streaming React 19 sur flux SSR tronqué.
    if (isReactStreamingError(event)) return null;
    // Drop le bug PostHog heatmaps null.tagName (SDK interne, non-actionnable).
    if (isPostHogHeatmapError(event)) return null;
    // Drop les bridges JS↔Java des in-app browsers Android (SnapTube/TikTok).
    if (isInAppBrowserBridgeError(event)) return null;
    // Drop les scripts injectés par le navigateur/extensions (aucune frame
    // dans nos bundles) et les bots qui hookent addEventListener.
    if (isInjectedScriptError(event)) return null;
    if (isAutomationHookError(event)) return null;
    // Autofill d'extension : invisible pour isInjectedScriptError à cause du
    // wrapper setTimeout de Sentry (cf. commentaire sur isAutofillExtensionError).
    if (isAutofillExtensionError(event)) return null;
    return event;
  },
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
